import type { JsonSchemaObject, PrerequisiteData } from '@onefootprint/request-types/dashboard';

// Typed off the field's JSON schema so an auto-filled required key passes the backend's schema check
// as-is (e.g. a numeric field seeds `0`, not `""`).
const placeholderForSchema = (schema?: JsonSchemaObject): string => {
  if (schema?.enum?.length) return JSON.stringify(schema.enum[0]);
  switch (schema?.type) {
    case 'integer':
    case 'number':
      return '0';
    case 'boolean':
      return 'false';
    case 'array':
      return '[]';
    case 'object':
      return '{}';
    default:
      return '""';
  }
};

// Vault prerequisites are excluded: they're satisfied by vaulted data, and the backend rejects a
// vault DI as a prerequisite_data key. Optional keys are commented out so an untouched template
// submits only what the playbook requires. `existingValues` (the editor's current parsed contents)
// is merged in — preserving values already entered, uncommenting any optional key now filled, and
// dropping keys that are no longer prerequisites — mirroring the AI decision node's input merge.
// Returns null when there's nothing to seed (only vault prerequisites, or none).
const buildPrerequisiteTemplate = (
  prerequisites: PrerequisiteData[],
  existingValues: Record<string, unknown> = {},
): string | null => {
  const lines = prerequisites
    .filter((p): p is Extract<PrerequisiteData, { kind: 'onboarding_data' }> => p.kind === 'onboarding_data')
    .map(p => {
      const hasValue = Object.hasOwn(existingValues, p.key);
      // A document prerequisite is a pointer string, so the string placeholder covers it.
      const value = hasValue ? JSON.stringify(existingValues[p.key]) : placeholderForSchema(p.schema);
      const entry = `"${p.key}": ${value},`;
      return p.isRequired || hasValue ? `  ${entry}` : `  // ${entry}`;
    });
  return lines.length > 0 ? `{\n${lines.join('\n')}\n}` : null;
};

export { buildPrerequisiteTemplate };
